import * as React from 'react'
import {
    Box,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded'

import useAutoTranslate from '../hooks/useAutoTranslate'

const PLUGIN_KEY = 'hospital'
const DEFAULT_CHECK_INTERVAL_MINUTES = '5'
const DEFAULT_HEAL_COIN_TROOPS = true
const DEFAULT_DISCARD_RUBY_TROOPS = true
const DEFAULT_REQUEST_ALLIANCE_HELP = true
const DEFAULT_HEAL_BATCH_SIZE = '10'

const SECTION_GROUPS = Object.freeze([
    {
        title: 'Main Castle & Outposts',
        helper: 'Choose which empire locations should run hospital healing.',
        items: [
            {
                id: 'main',
                configKey: 'section:main',
                label: 'Main Castle',
                helper: 'Allow hospital healing in your main castle.',
                defaultEnabled: true
            },
            {
                id: 'outpost1',
                configKey: 'section:outpost1',
                label: 'Outpost 1',
                helper: 'Allow hospital healing in the first outpost.',
                defaultEnabled: true
            },
            {
                id: 'outpost2',
                configKey: 'section:outpost2',
                label: 'Outpost 2',
                helper: 'Allow hospital healing in the second outpost.',
                defaultEnabled: true
            },
            {
                id: 'outpost3',
                configKey: 'section:outpost3',
                label: 'Outpost 3',
                helper: 'Allow hospital healing in the third outpost.',
                defaultEnabled: true
            }
        ]
    },
    {
        title: 'Kingdoms, Capital & Metro',
        helper: 'Control healing separately for each world and special city.',
        items: [
            {
                id: 'ice',
                configKey: 'section:ice',
                label: 'Ice Kingdom',
                helper: 'Allow hospital healing in Everwinter Glacier.',
                defaultEnabled: true
            },
            {
                id: 'desert',
                configKey: 'section:desert',
                label: 'Desert Kingdom',
                helper: 'Allow hospital healing in Burning Sands.',
                defaultEnabled: true
            },
            {
                id: 'fire',
                configKey: 'section:fire',
                label: 'Fire Kingdom',
                helper: 'Allow hospital healing in Fire Peaks.',
                defaultEnabled: true
            },
            {
                id: 'storm',
                configKey: 'section:storm',
                label: 'Storm Islands',
                helper: 'Allow hospital healing in Storm Islands.',
                defaultEnabled: false
            },
            {
                id: 'capital',
                configKey: 'section:capital',
                label: 'Capital',
                helper: 'Allow hospital healing in alliance capitals.',
                defaultEnabled: false
            },
            {
                id: 'metro',
                configKey: 'section:metro',
                label: 'Metro',
                helper: 'Allow hospital healing in metros.',
                defaultEnabled: true
            }
        ]
    }
])

const ALL_SECTION_ITEMS = SECTION_GROUPS.flatMap(group => group.items)

const cloneValue = value =>
    typeof structuredClone === 'function' ?
        structuredClone(value) :
        JSON.parse(JSON.stringify(value ?? {}))

const normalizePositiveNumberText = (value, fallback = '') => {
    if ([undefined, null, ''].includes(value))
        return fallback

    const parsed = Number.parseInt(String(value), 10)
    return Number.isFinite(parsed) && parsed >= 0 ? String(parsed) : fallback
}

const normalizeBoolean = (value, fallback) =>
    typeof value === 'boolean' ? value : fallback

const normalizeSectionConfig = (value, defaults) => ({
    enabled: normalizeBoolean(value?.enabled, defaults.defaultEnabled)
})

const FIELD_INPUT_SX = {
    '& .MuiOutlinedInput-root': {
        minHeight: 42,
        borderRadius: '10px',
        backgroundColor: 'rgba(24, 36, 57, 0.76)'
    },
    '& .MuiInputBase-input': {
        fontSize: '1.01rem',
        fontWeight: 700,
        color: '#f5f7ff'
    }
}

const PANEL_CARD_SX = {
    p: { xs: 2, md: 2.5 },
    borderRadius: '18px',
    border: '1px solid rgba(129, 146, 178, 0.14)',
    backgroundColor: 'rgba(20, 31, 52, 0.5)'
}

function Helper({ children }) {
    return (
        <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mt: 0.78 }}>
            <InfoOutlinedIcon sx={{ fontSize: 12.5, color: '#95a5c4' }} />
            <Typography sx={{ fontSize: '0.885rem', color: '#95a5c4', lineHeight: 1.35 }}>{children}</Typography>
        </Stack>
    )
}

function FieldTitle({ children }) {
    return (
        <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800, mb: 0.9 }}>
            {children}
        </Typography>
    )
}

function StyledSwitch({ checked, onChange }) {
    return (
        <Switch
            checked={checked}
            onChange={event => onChange(event.target.checked)}
            sx={{
                width: 48,
                height: 29,
                p: 0,
                '& .MuiSwitch-switchBase': {
                    p: '2.5px',
                    '&.Mui-checked': {
                        transform: 'translateX(18px)',
                        color: '#ffffff'
                    },
                    '&.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#2f7cff',
                        opacity: 1
                    }
                },
                '& .MuiSwitch-thumb': {
                    width: 24,
                    height: 24,
                    boxShadow: 'none'
                },
                '& .MuiSwitch-track': {
                    opacity: 1,
                    borderRadius: '999px',
                    backgroundColor: 'rgba(111, 123, 143, 0.9)'
                }
            }}
        />
    )
}

function ToggleField({ checked, onChange, label, helper }) {
    return (
        <Box sx={{ minHeight: helper ? 56 : 34 }}>
            <Stack direction="row" spacing={1.1} alignItems="center" sx={{ pt: helper ? 0.25 : 0 }}>
                <StyledSwitch checked={checked} onChange={onChange} />
                <Typography sx={{ color: '#f5f7ff', fontSize: '0.995rem', fontWeight: 800, letterSpacing: '-0.01em' }}>
                    {label}
                </Typography>
            </Stack>
            {helper ? <Helper>{helper}</Helper> : null}
        </Box>
    )
}

function SectionHeading({ children }) {
    return (
        <Stack direction="row" alignItems="center" spacing={1.3} sx={{ mb: 2.2 }}>
            <Typography sx={{ color: '#f5f7ff', fontSize: '1.05rem', fontWeight: 800 }}>{children}</Typography>
            <Box sx={{ flex: 1, height: 1, backgroundColor: 'rgba(129, 146, 178, 0.22)' }} />
        </Stack>
    )
}

export default function HospitalHealerSettingsPanel({ userPlugins, onChange, appLanguage = 'en', hideHeader = false }) {
    const pluginState = React.useMemo(
        () => userPlugins?.[PLUGIN_KEY] && typeof userPlugins[PLUGIN_KEY] === 'object' ? userPlugins[PLUGIN_KEY] : {},
        [userPlugins]
    )
    const locationConfigs = React.useMemo(
        () => pluginState?.locationConfigs && typeof pluginState.locationConfigs === 'object' ? pluginState.locationConfigs : {},
        [pluginState]
    )

    const t = useAutoTranslate(appLanguage, [
        'Hospital Healer Settings',
        'Configure military hospital automation behavior per castle.',
        'Automate troop recovery while keeping each location under your control.',
        'Automation Rules',
        'Location Coverage',
        'Sections disabled here are skipped even if the hospital has troops waiting.',
        'Check Interval (Minutes)',
        'How often hospital automation runs.',
        'Discard Ruby Troops',
        'Discard hospital troops that heal with rubies.',
        'Heal Coin Troops',
        'Heal hospital troops that heal with coins.',
        'Heal Batch Size',
        'How many troops to put into each hospital recovery slot.',
        'Request Alliance Help',
        'Request alliance help once per castle for hospital heals.',
        ...SECTION_GROUPS.flatMap(group => [group.title, group.helper]),
        ...ALL_SECTION_ITEMS.flatMap(section => [section.label, section.helper])
    ])

    const config = React.useMemo(() => {
        const fallbackIntervalMinutes = normalizePositiveNumberText(
            pluginState?.interval ? Math.max(1, Math.round(Number.parseInt(String(pluginState.interval), 10) / 60)) : undefined,
            DEFAULT_CHECK_INTERVAL_MINUTES
        )

        return {
            checkIntervalMinutes: normalizePositiveNumberText(pluginState?.checkIntervalMinutes, fallbackIntervalMinutes),
            discardRubyTroops: normalizeBoolean(pluginState?.discardRubyTroops, normalizeBoolean(pluginState?.enableDelete, DEFAULT_DISCARD_RUBY_TROOPS)),
            healCoinTroops: normalizeBoolean(pluginState?.healCoinTroops, normalizeBoolean(pluginState?.enableHeal, DEFAULT_HEAL_COIN_TROOPS)),
            healBatchSize: normalizePositiveNumberText(pluginState?.healBatchSize, DEFAULT_HEAL_BATCH_SIZE),
            requestAllianceHelp: normalizeBoolean(pluginState?.requestAllianceHelp, DEFAULT_REQUEST_ALLIANCE_HELP),
            sections: Object.fromEntries(
                ALL_SECTION_ITEMS.map(section => [
                    section.id,
                    normalizeSectionConfig(locationConfigs[section.configKey], section)
                ])
            )
        }
    }, [
        locationConfigs,
        pluginState
    ])

    const commit = React.useCallback(nextConfig => {
        const nextPlugins = cloneValue(userPlugins ?? {})
        const intervalMinutes = normalizePositiveNumberText(nextConfig.checkIntervalMinutes, DEFAULT_CHECK_INTERVAL_MINUTES)

        nextPlugins[PLUGIN_KEY] ??= {}
        nextPlugins[PLUGIN_KEY].checkIntervalMinutes = intervalMinutes
        nextPlugins[PLUGIN_KEY].discardRubyTroops = Boolean(nextConfig.discardRubyTroops)
        nextPlugins[PLUGIN_KEY].healCoinTroops = Boolean(nextConfig.healCoinTroops)
        nextPlugins[PLUGIN_KEY].healBatchSize = normalizePositiveNumberText(nextConfig.healBatchSize, DEFAULT_HEAL_BATCH_SIZE)
        nextPlugins[PLUGIN_KEY].requestAllianceHelp = Boolean(nextConfig.requestAllianceHelp)
        nextPlugins[PLUGIN_KEY].locationConfigs = Object.fromEntries(
            ALL_SECTION_ITEMS.map(section => [
                section.configKey,
                {
                    enabled: Boolean(nextConfig.sections[section.id]?.enabled)
                }
            ])
        )

        nextPlugins[PLUGIN_KEY].enableDelete = Boolean(nextConfig.discardRubyTroops)
        nextPlugins[PLUGIN_KEY].enableHeal = Boolean(nextConfig.healCoinTroops)
        nextPlugins[PLUGIN_KEY].interval = String(Math.max(1, Number.parseInt(intervalMinutes, 10) || Number(DEFAULT_CHECK_INTERVAL_MINUTES)) * 60)

        onChange(nextPlugins)
    }, [onChange, userPlugins])

    const update = updater => {
        const nextConfig = cloneValue(config)
        updater(nextConfig)
        commit(nextConfig)
    }

    return (
        <Paper
            sx={{
                p: { xs: 2.2, md: 3.1 },
                borderRadius: '22px',
                border: '1px solid rgba(129, 146, 178, 0.18)',
                background: 'linear-gradient(180deg, rgba(27, 40, 63, 0.9) 0%, rgba(24, 35, 56, 0.86) 100%)',
                boxShadow: '0 22px 46px rgba(7, 13, 26, 0.22)'
            }}
        >
            <Stack spacing={3.2}>
                <Box sx={PANEL_CARD_SX}>
                    <Stack spacing={3}>
                        {!hideHeader && (
                            <Box>
                                <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 0.55 }}>
                                    <LocalHospitalRoundedIcon sx={{ color: '#f5f7ff', fontSize: 22 }} />
                                    <Typography sx={{ color: '#f5f7ff', fontSize: '1.42rem', fontWeight: 800 }}>
                                        {t('Hospital Healer Settings')}
                                    </Typography>
                                </Stack>
                                <Typography sx={{ color: '#9fb0cc', fontSize: '1.02rem' }}>
                                    {t('Configure military hospital automation behavior per castle.')}
                                </Typography>
                                <Helper>{t('Automate troop recovery while keeping each location under your control.')}</Helper>
                            </Box>
                        )}

                        <Box>
                            <SectionHeading>{t('Automation Rules')}</SectionHeading>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
                                    columnGap: { xs: 0, xl: 4.5 },
                                    rowGap: 2.45
                                }}
                            >
                                <Box>
                                    <FieldTitle>{t('Check Interval (Minutes)')}</FieldTitle>
                                    <TextField
                                        fullWidth
                                        value={config.checkIntervalMinutes}
                                        onChange={event => update(next => { next.checkIntervalMinutes = event.target.value })}
                                        sx={FIELD_INPUT_SX}
                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                    />
                                    <Helper>{t('How often hospital automation runs.')}</Helper>
                                </Box>

                                <ToggleField
                                    checked={config.discardRubyTroops}
                                    onChange={value => update(next => { next.discardRubyTroops = value })}
                                    label={t('Discard Ruby Troops')}
                                    helper={t('Discard hospital troops that heal with rubies.')}
                                />

                                <ToggleField
                                    checked={config.healCoinTroops}
                                    onChange={value => update(next => { next.healCoinTroops = value })}
                                    label={t('Heal Coin Troops')}
                                    helper={t('Heal hospital troops that heal with coins.')}
                                />

                                <Box>
                                    <FieldTitle>{t('Heal Batch Size')}</FieldTitle>
                                    <TextField
                                        fullWidth
                                        value={config.healBatchSize}
                                        onChange={event => update(next => { next.healBatchSize = event.target.value })}
                                        sx={FIELD_INPUT_SX}
                                        inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                                    />
                                    <Helper>{t('How many troops to put into each hospital recovery slot.')}</Helper>
                                </Box>

                                <ToggleField
                                    checked={config.requestAllianceHelp}
                                    onChange={value => update(next => { next.requestAllianceHelp = value })}
                                    label={t('Request Alliance Help')}
                                    helper={t('Request alliance help once per castle for hospital heals.')}
                                />
                            </Box>
                        </Box>
                    </Stack>
                </Box>

                <Box sx={PANEL_CARD_SX}>
                    <Stack spacing={2.6}>
                        <Box>
                            <SectionHeading>{t('Location Coverage')}</SectionHeading>
                            <Typography sx={{ color: '#95a5c4', fontSize: '0.94rem', lineHeight: 1.5 }}>
                                {t('Sections disabled here are skipped even if the hospital has troops waiting.')}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', xl: 'repeat(2, minmax(0, 1fr))' },
                                gap: 2.4
                            }}
                        >
                            {SECTION_GROUPS.map(group => (
                                <Box
                                    key={group.title}
                                    sx={{
                                        p: { xs: 1.6, md: 1.9 },
                                        borderRadius: '16px',
                                        border: '1px solid rgba(129, 146, 178, 0.12)',
                                        backgroundColor: 'rgba(18, 28, 47, 0.58)'
                                    }}
                                >
                                    <Stack spacing={2.15}>
                                        <Box>
                                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 800 }}>
                                                {t(group.title)}
                                            </Typography>
                                            <Typography sx={{ mt: 0.55, color: '#95a5c4', fontSize: '0.91rem', lineHeight: 1.45 }}>
                                                {t(group.helper)}
                                            </Typography>
                                        </Box>

                                        <Stack spacing={1.65}>
                                            {group.items.map(section => (
                                                <ToggleField
                                                    key={section.id}
                                                    checked={config.sections[section.id]?.enabled}
                                                    onChange={value => update(next => { next.sections[section.id].enabled = value })}
                                                    label={t(section.label)}
                                                    helper={t(section.helper)}
                                                />
                                            ))}
                                        </Stack>
                                    </Stack>
                                </Box>
                            ))}
                        </Box>
                    </Stack>
                </Box>
            </Stack>
        </Paper>
    )
}
